import { useState, useCallback, useMemo, useEffect, useRef } from 'react';

// ── 题库数据 ──
interface QuizQuestion {
  id: string;
  question: string;
  options: string[];
  correctIndex: number;
  explanation: string;
  category: string;
  difficulty: 'easy' | 'medium' | 'hard';
}

const QUIZ_BANK: QuizQuestion[] = [
  {
    id: 'q1',
    question: 'RWA 的全称是什么？',
    options: ['Real World Assets', 'Regulated Web Assets', 'Retail Wealth Allocation', 'Risk-Weighted Assets'],
    correctIndex: 0,
    explanation: 'RWA = Real World Assets（真实世界资产），指通过区块链代币化技术将现实世界资产转化为可在链上流通的数字代币。',
    category: '基础概念',
    difficulty: 'easy',
  },
  {
    id: 'q2',
    question: '香港SFC持牌机构中，哪类牌照负责"资产管理"？',
    options: ['1类牌照', '4类牌照', '7类牌照', '9类牌照'],
    correctIndex: 3,
    explanation: 'SFC 9类牌照（资产管理）是RWA发行中最常见的牌照之一，负责管理投资组合。1类为经纪，4类为投资顾问。',
    category: '监管机构',
    difficulty: 'medium',
  },
  {
    id: 'q3',
    question: 'ERC-3643（T-REX协议）的核心优势是什么？',
    options: ['Gas费最低', '内置链上身份注册表+合规模块', '支持批量铸造NFT', '兼容所有区块链'],
    correctIndex: 1,
    explanation: 'ERC-3643内置链上身份注册表（Identity Registry）和合规模块（Compliance Module），可自动执行KYC白名单、国家限制、持仓上限等合规规则，是目前RWA领域最推荐的合规架构。',
    category: 'ERC标准体系',
    difficulty: 'medium',
  },
  {
    id: 'q4',
    question: '香港"专业投资者"的个人净资产门槛是多少？',
    options: ['≥500万港元', '≥800万港元', '≥1000万港元', '≥2000万港元'],
    correctIndex: 1,
    explanation: '香港专业投资者（PI）个人须满足：净资产≥800万港元（含主要居所，但不超过500万）或受管理投资组合≥800万港元。只有PI才能认购香港合规框架下的RWA/STO产品。',
    category: '机构与角色',
    difficulty: 'easy',
  },
  {
    id: 'q5',
    question: 'FATF旅行规则要求虚拟资产转账超过多少时须传递身份信息？',
    options: ['500美元', '1000美元', '3000美元', '10000美元'],
    correctIndex: 1,
    explanation: 'FATF旅行规则要求：虚拟资产服务提供商在转账超过1000美元/欧元时，须随交易传递发送方和接收方的身份信息，是全球虚拟资产AML合规的核心规则。',
    category: '合规与监管',
    difficulty: 'medium',
  },
  {
    id: 'q6',
    question: 'ERC-4626 是什么标准？',
    options: ['NFT租赁标准', '代币化金库标准', '半同质化代币标准', '元交易标准'],
    correctIndex: 1,
    explanation: 'ERC-4626是代币化金库标准，标准化"存入基础资产→获得份额代币→赎回底层资产"的流程。RWA收益池（如美债收益池）的最佳标准，份额代币随底层资产价值自动增值。',
    category: 'ERC标准体系',
    difficulty: 'medium',
  },
  {
    id: 'q7',
    question: '"真实出售"（True Sale）在RWA架构中的核心作用是什么？',
    options: ['降低Gas费用', '实现资产破产隔离', '提高代币流动性', '简化KYC流程'],
    correctIndex: 1,
    explanation: '"真实出售"是发起人将底层资产完全转让给SPV的法律行为，须确保资产在法律上与发起人破产隔离。即使发起方破产，SPV资产仍独立存在，保护代币持有人权益。',
    category: '基础概念',
    difficulty: 'hard',
  },
  {
    id: 'q8',
    question: 'SPV（特殊目的载体）在RWA发行中的主要作用是什么？',
    options: ['提供技术开发服务', '隔离底层资产风险并持有资产发行代币', '负责KYC审核', '管理二级市场交易'],
    correctIndex: 1,
    explanation: 'SPV是发行RWA时常用的离岸法律实体（如开曼群岛公司），用于隔离底层资产风险、持有基础资产并发行代币。SPV是RWA发行架构的核心法律容器。',
    category: '机构与角色',
    difficulty: 'easy',
  },
  {
    id: 'q9',
    question: 'Chainlink在RWA生态中扮演什么角色？',
    options: ['智能合约审计机构', '去中心化预言机，提供链下数据', '代币发行平台', 'KYC服务商'],
    correctIndex: 1,
    explanation: 'Chainlink是最主要的去中心化预言机，将链下的资产价格、合规状态、利率等数据实时输入链上智能合约。例如为Ondo Finance的OUSG国债代币提供实时国债价格数据。',
    category: '技术与代币',
    difficulty: 'easy',
  },
  {
    id: 'q10',
    question: 'MiCA法规主要适用于哪类资产？',
    options: ['所有加密资产包括证券型代币', '仅适用于稳定币和实用型代币，不含证券型代币', '仅适用于NFT', '仅适用于CBDC'],
    correctIndex: 1,
    explanation: 'MiCA（欧盟加密资产市场监管条例）主要适用于稳定币（ART/EMT）和实用型代币。证券型RWA代币不纳入MiCA，须在MiFID II框架下监管。发行前须经法律意见书确认代币定性。',
    category: '合规与监管',
    difficulty: 'hard',
  },
  {
    id: 'q11',
    question: 'ERC-20代币标准的核心特征是什么？',
    options: ['每个代币唯一不可互换', '所有代币完全相同可拆分互换', '支持多资产混合管理', '内置KYC白名单'],
    correctIndex: 1,
    explanation: 'ERC-20是以太坊同质化代币标准，所有代币完全相同、可拆分、可互换（1枚=1枚）。是RWA份额代币、稳定币、碳信用的基础规范，但本身无合规控制，RWA场景须叠加合规层。',
    category: 'ERC标准体系',
    difficulty: 'easy',
  },
  {
    id: 'q12',
    question: 'ZKP（零知识证明）在RWA合规中的应用场景是什么？',
    options: ['加速交易确认速度', '证明投资者资质而不暴露具体资产数额', '降低智能合约Gas费', '实现跨链资产转移'],
    correctIndex: 1,
    explanation: 'ZKP可实现"证明投资者是合格投资者，但无需向平台暴露具体资产数额"。例如证明净资产≥800万港元而无需提交完整银行流水，保护隐私同时满足合规要求。',
    category: '技术与代币',
    difficulty: 'hard',
  },
  {
    id: 'q13',
    question: 'TVL（Total Value Locked）是什么指标？',
    options: ['代币总发行量', '链上锁定总价值，衡量协议规模', '年化收益率', '交易手续费总额'],
    correctIndex: 1,
    explanation: 'TVL = Total Value Locked（链上锁定总价值），衡量DeFi协议或RWA平台中锁定资产规模的指标，体现协议的规模、市场信任度和流动性深度。可在DeFiLlama实时查询。',
    category: '金融工具',
    difficulty: 'easy',
  },
  {
    id: 'q14',
    question: '原子结算（Atomic Settlement）的核心优势是什么？',
    options: ['降低交易手续费', '资产转让和付款在同一笔交易中同步完成，消除结算风险', '提高代币流动性', '简化KYC流程'],
    correctIndex: 1,
    explanation: '原子结算使资产转让和付款在同一区块中不可分割地完成，任何一方都无法只收款不交货，消除传统T+2结算中的结算风险。HKMA Project Ensemble正在测试此场景。',
    category: 'DeFi与流动性',
    difficulty: 'medium',
  },
  {
    id: 'q15',
    question: 'ERC-721（NFT）与ERC-1155的主要区别是什么？',
    options: [
      'ERC-721支持批量铸造，ERC-1155不支持',
      'ERC-721每个代币唯一，ERC-1155一个合约可同时管理多种FT+NFT',
      'ERC-721有合规控制，ERC-1155没有',
      'ERC-721只能在以太坊使用，ERC-1155可跨链',
    ],
    correctIndex: 1,
    explanation: 'ERC-721每个代币唯一不可互换（NFT）；ERC-1155是多资产混合标准，一个合约可同时管理无数种同质化（FT）+非同质化（NFT）资产，支持批量转账，Gas效率远高于ERC-721。',
    category: 'ERC标准体系',
    difficulty: 'medium',
  },
  {
    id: 'q16',
    question: 'CBDC中"批发型"和"零售型"的主要区别是什么？',
    options: [
      '批发型面向公众，零售型面向机构',
      '批发型用于机构间结算，零售型面向公众使用',
      '批发型基于区块链，零售型基于传统数据库',
      '批发型由央行发行，零售型由商业银行发行',
    ],
    correctIndex: 1,
    explanation: 'CBDC分为批发型（机构间结算用，如HKMA测试的批发型e-HKD用于RWA结算）和零售型（公众使用，如中国e-CNY主要用于零售支付）两大方向。',
    category: '金融工具',
    difficulty: 'medium',
  },
  {
    id: 'q17',
    question: '美国SEC的"豪威测试"用于判断什么？',
    options: ['判断代币是否符合ERC标准', '判断资产是否构成证券', '判断交易所是否合规', '判断KYC是否充分'],
    correctIndex: 1,
    explanation: '豪威测试（Howey Test）通过四要素判断资产是否为证券：具有金钱投入+共同事业+预期利润+依赖他人努力则为证券。SEC主张绝大多数加密资产（含RWA代币）属于证券。',
    category: '监管机构',
    difficulty: 'hard',
  },
  {
    id: 'q18',
    question: 'DID（去中心化数字身份）在RWA合规中的核心价值是什么？',
    options: ['降低Gas费', '一次KYC认证，多平台通行', '提高交易速度', '实现匿名交易'],
    correctIndex: 1,
    explanation: 'DID可与KYC结果绑定，实现"一次认证、全平台通行"。投资者在持牌KYC服务商完成认证后，获得绑定DID的"合格投资者凭证"，在支持该DID标准的所有RWA平台均可直接投资，无需重复KYC。',
    category: '技术与代币',
    difficulty: 'medium',
  },
  {
    id: 'q19',
    question: '碳信用代币化中，"双重计算"问题指的是什么？',
    options: ['同一碳信用被两个买家都用于碳抵消', '碳信用价格被计算两次', '同一项目获得两次认证', '碳信用在两条链上同时流通'],
    correctIndex: 0,
    explanation: '双重计算是碳信用代币化的核心合规风险：同一碳信用不能被两个买家都用于碳抵消。代币化须确保底层碳信用已被注销（retire），注销凭证须链上存证，防止同一碳信用被重复使用。',
    category: '金融工具',
    difficulty: 'hard',
  },
  {
    id: 'q20',
    question: 'ERC-2771（元交易标准）解决了什么用户体验问题？',
    options: ['代币转账速度慢', '用户无需持有ETH即可与合约交互（Gas代付）', '代币无法跨链转移', 'NFT无法批量铸造'],
    correctIndex: 1,
    explanation: 'ERC-2771允许第三方（如RWA平台）代替用户支付Gas费，用户只需签名授权，平台承担Gas成本。极大降低非加密原生投资者的使用门槛——投资者无需持有ETH即可交互RWA智能合约。',
    category: 'ERC标准体系',
    difficulty: 'medium',
  },
];

// ── 工具函数 ──
function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const DIFFICULTY_LABELS: Record<string, { label: string; color: string }> = {
  easy: { label: '基础', color: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
  medium: { label: '进阶', color: 'text-amber-600 bg-amber-50 border-amber-200' },
  hard: { label: '专家', color: 'text-rose-600 bg-rose-50 border-rose-200' },
};

// ── 排行榜数据类型 ──
interface ScoreRecord {
  score: number;
  total: number;
  percentage: number;
  difficulty: string;
  date: string;
  timestamp: number;
}

const LEADERBOARD_KEY = 'rwa99_quiz_leaderboard';

function loadLeaderboard(): ScoreRecord[] {
  try {
    const raw = localStorage.getItem(LEADERBOARD_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveScore(record: ScoreRecord) {
  try {
    const existing = loadLeaderboard();
    const updated = [record, ...existing].slice(0, 20);
    localStorage.setItem(LEADERBOARD_KEY, JSON.stringify(updated));
  } catch (e) {
    void e;
  }
}

// ── 错题本数据类型 ──
interface WrongRecord {
  questionId: string;
  question: string;
  options: string[];
  correctIndex: number;
  explanation: string;
  category: string;
  difficulty: 'easy' | 'medium' | 'hard';
  wrongCount: number;
  lastWrongDate: string;
}

const WRONG_BOOK_KEY = 'rwa99_quiz_wrong_book';

function loadWrongBook(): WrongRecord[] {
  try {
    const raw = localStorage.getItem(WRONG_BOOK_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveWrongBook(records: WrongRecord[]) {
  try {
    localStorage.setItem(WRONG_BOOK_KEY, JSON.stringify(records));
  } catch (e) {
    void e;
  }
}

function addToWrongBook(question: QuizQuestion) {
  const existing = loadWrongBook();
  const idx = existing.findIndex((r) => r.questionId === question.id);
  const date = new Date().toLocaleDateString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  if (idx >= 0) {
    existing[idx].wrongCount++;
    existing[idx].lastWrongDate = date;
  } else {
    existing.push({
      questionId: question.id,
      question: question.question,
      options: question.options,
      correctIndex: question.correctIndex,
      explanation: question.explanation,
      category: question.category,
      difficulty: question.difficulty,
      wrongCount: 1,
      lastWrongDate: date,
    });
  }
  saveWrongBook(existing);
}

function removeFromWrongBook(questionId: string) {
  const existing = loadWrongBook().filter((r) => r.questionId !== questionId);
  saveWrongBook(existing);
}

// ── 主组件 ──
interface GlossaryQuizProps {
  onClose: () => void;
}

type QuizMode = 'config' | 'playing' | 'result' | 'leaderboard' | 'wrongbook' | 'wrongbook_practice';

export default function GlossaryQuiz({ onClose }: GlossaryQuizProps) {
  const [mode, setMode] = useState<QuizMode>('config');
  const [questionCount, setQuestionCount] = useState(10);
  const [selectedDifficulty, setSelectedDifficulty] = useState<'all' | 'easy' | 'medium' | 'hard'>('all');
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState<(number | null)[]>([]);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [timeLeft, setTimeLeft] = useState(30);
  const [timerActive, setTimerActive] = useState(false);
  const [leaderboard, setLeaderboard] = useState<ScoreRecord[]>([]);
  const [shareSuccess, setShareSuccess] = useState(false);
  const [wrongBook, setWrongBook] = useState<WrongRecord[]>([]);
  const [wrongBookFilter, setWrongBookFilter] = useState<string>('全部');

  useEffect(() => {
    setLeaderboard(loadLeaderboard());
    setWrongBook(loadWrongBook());
  }, []);

  // 计时器
  useEffect(() => {
    if (!timerActive || mode !== 'playing') return;
    if (timeLeft <= 0) {
      handleAnswer(-1);
      return;
    }
    const t = setTimeout(() => setTimeLeft((v) => v - 1), 1000);
    return () => clearTimeout(t);
  }, [timerActive, timeLeft, mode]);

  const startQuiz = useCallback(() => {
    let pool = QUIZ_BANK;
    if (selectedDifficulty !== 'all') {
      pool = pool.filter((q) => q.difficulty === selectedDifficulty);
    }
    const shuffled = shuffle(pool).slice(0, Math.min(questionCount, pool.length));
    setQuestions(shuffled);
    setAnswers(new Array(shuffled.length).fill(null));
    setCurrentIdx(0);
    setSelectedOption(null);
    setShowExplanation(false);
    setTimeLeft(30);
    setTimerActive(true);
    setMode('playing');
  }, [questionCount, selectedDifficulty]);

  const handleAnswer = useCallback((optionIdx: number) => {
    if (selectedOption !== null) return;
    setTimerActive(false);
    setSelectedOption(optionIdx);
    setShowExplanation(true);
    setAnswers((prev) => {
      const next = [...prev];
      next[currentIdx] = optionIdx;
      return next;
    });
    // 答错时自动加入错题本
    const q = questions[currentIdx];
    if (q && optionIdx !== q.correctIndex) {
      addToWrongBook(q);
      setWrongBook(loadWrongBook());
    }
  }, [selectedOption, currentIdx, questions]);

  const handleNext = useCallback(() => {
    if (currentIdx + 1 >= questions.length) {
      setMode('result');
      setTimerActive(false);
    } else {
      setCurrentIdx((v) => v + 1);
      setSelectedOption(null);
      setShowExplanation(false);
      setTimeLeft(30);
      setTimerActive(true);
    }
  }, [currentIdx, questions.length]);

  const score = useMemo(() => {
    return answers.filter((a, i) => a === questions[i]?.correctIndex).length;
  }, [answers, questions]);

  // 保存成绩到排行榜
  const saveCurrentScore = useCallback(() => {
    if (questions.length === 0) return;
    const percentage = Math.round((score / questions.length) * 100);
    const diffLabel = selectedDifficulty === 'all' ? '全部' : selectedDifficulty === 'easy' ? '基础' : selectedDifficulty === 'medium' ? '进阶' : '专家';
    const record: ScoreRecord = {
      score,
      total: questions.length,
      percentage,
      difficulty: diffLabel,
      date: new Date().toLocaleDateString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }),
      timestamp: Date.now(),
    };
    saveScore(record);
    setLeaderboard(loadLeaderboard());
  }, [score, questions.length, selectedDifficulty]);

  // 进入结果页时自动保存
  useEffect(() => {
    if (mode === 'result') {
      saveCurrentScore();
    }
  }, [mode]);

  const currentQ = questions[currentIdx];

  // 生成分享文本
  const generateShareText = useCallback((pct: number, sc: number, total: number, diff: string) => {
    const stars = pct >= 90 ? '⭐⭐⭐⭐⭐' : pct >= 70 ? '⭐⭐⭐⭐' : pct >= 50 ? '⭐⭐⭐' : '⭐⭐';
    const level = pct >= 90 ? 'RWA专家' : pct >= 70 ? '进阶学员' : pct >= 50 ? '基础掌握' : '继续加油';
    const bar = Array.from({ length: 10 }, (_, i) => i < Math.round(pct / 10) ? '█' : '░').join('');
    return [
      '━━━━━━━━━━━━━━━━━━━━',
      '📊 RWA词条知识测试成绩单',
      '━━━━━━━━━━━━━━━━━━━━',
      `${stars} ${level}`,
      `正确率：${pct}%  [${bar}]`,
      `答对：${sc}/${total} 题  难度：${diff}`,
      `测试时间：${new Date().toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}`,
      '━━━━━━━━━━━━━━━━━━━━',
      '🔗 来自 RWA99 · 全球RWA知识平台',
    ].join('\n');
  }, []);

  // 分享成绩
  const handleShare = useCallback(async (pct: number, sc: number, total: number, diff: string) => {
    const text = generateShareText(pct, sc, total, diff);
    try {
      if (navigator.share) {
        await navigator.share({ title: 'RWA词条测试成绩', text });
      } else {
        await navigator.clipboard.writeText(text);
        setShareSuccess(true);
        setTimeout(() => setShareSuccess(false), 2500);
      }
    } catch {
      // fallback: download as txt
      const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `RWA测试成绩_${pct}%.txt`;
      a.click();
      URL.revokeObjectURL(url);
    }
  }, [generateShareText]);

  // ── 配置页 ──
  if (mode === 'config') {
    const bestScore = leaderboard.length > 0 ? Math.max(...leaderboard.map((r) => r.percentage)) : null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
        <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl">
          <div className="bg-gradient-to-r from-[#2E1065] to-[#4C1D95] px-6 py-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl">
                  <i className="ri-brain-line text-white text-xl"></i>
                </div>
                <div>
                  <div className="text-white font-black text-lg">词条知识测试</div>
                  <div className="text-white/60 text-xs">检验你的RWA学习成果</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {bestScore !== null && (
                  <div className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-400/20 rounded-full">
                    <i className="ri-trophy-line text-amber-300 text-sm"></i>
                    <span className="text-amber-200 text-xs font-black">最高 {bestScore}%</span>
                  </div>
                )}
                <button
                  onClick={() => setMode('wrongbook')}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/25 cursor-pointer transition-colors relative"
                  title="错题本"
                >
                  <i className="ri-error-warning-line text-white text-sm"></i>
                  {wrongBook.length > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-white text-[9px] font-black flex items-center justify-center">
                      {wrongBook.length > 9 ? '9+' : wrongBook.length}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setMode('leaderboard')}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/25 cursor-pointer transition-colors"
                  title="查看排行榜"
                >
                  <i className="ri-bar-chart-2-line text-white text-sm"></i>
                </button>
                <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/25 cursor-pointer transition-colors">
                  <i className="ri-close-line text-white"></i>
                </button>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-5">
            {/* 题目数量 */}
            <div>
              <div className="text-sm font-bold text-gray-700 mb-3">题目数量</div>
              <div className="grid grid-cols-4 gap-2">
                {[5, 10, 15, 20].map((n) => (
                  <button
                    key={n}
                    onClick={() => setQuestionCount(n)}
                    className={`py-2.5 rounded-xl text-sm font-bold cursor-pointer transition-all ${
                      questionCount === n
                        ? 'bg-[#7C3AED] text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {n}题
                  </button>
                ))}
              </div>
            </div>

            {/* 难度选择 */}
            <div>
              <div className="text-sm font-bold text-gray-700 mb-3">难度筛选</div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: 'all', label: '全部难度', icon: 'ri-apps-line', count: QUIZ_BANK.length },
                  { value: 'easy', label: '基础', icon: 'ri-seedling-line', count: QUIZ_BANK.filter((q) => q.difficulty === 'easy').length },
                  { value: 'medium', label: '进阶', icon: 'ri-fire-line', count: QUIZ_BANK.filter((q) => q.difficulty === 'medium').length },
                  { value: 'hard', label: '专家', icon: 'ri-award-line', count: QUIZ_BANK.filter((q) => q.difficulty === 'hard').length },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setSelectedDifficulty(opt.value as typeof selectedDifficulty)}
                    className={`flex items-center gap-2 px-4 py-3 rounded-xl cursor-pointer transition-all border ${
                      selectedDifficulty === opt.value
                        ? 'bg-[#7C3AED]/10 border-[#7C3AED]/40 text-[#7C3AED]'
                        : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <i className={`${opt.icon} text-sm`}></i>
                    <span className="text-sm font-semibold">{opt.label}</span>
                    <span className="ml-auto text-xs opacity-60">{opt.count}题</span>
                  </button>
                ))}
              </div>
            </div>

            {/* 题库统计 */}
            <div className="bg-[#F8F7FF] rounded-xl p-4 flex items-center gap-3">
              <i className="ri-database-2-line text-[#7C3AED] text-lg"></i>
              <div>
                <div className="text-sm font-bold text-gray-800">题库共 {QUIZ_BANK.length} 道题</div>
                <div className="text-xs text-gray-500">覆盖8大专题 · 每次随机抽取 · 含详细解析</div>
              </div>
            </div>

            <button
              onClick={startQuiz}
              className="w-full py-3.5 bg-[#7C3AED] text-white rounded-xl font-bold text-base hover:bg-[#6D28D9] cursor-pointer transition-colors whitespace-nowrap"
            >
              <i className="ri-play-circle-line mr-2"></i>
              开始测试
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── 排行榜页 ──
  if (mode === 'leaderboard') {
    const bestRecord = leaderboard.length > 0 ? leaderboard.reduce((a, b) => a.percentage >= b.percentage ? a : b) : null;
    // 进步曲线数据（最近10次，时间正序）
    const curveData = [...leaderboard].reverse().slice(-10);

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4 overflow-y-auto py-6">
        <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl">
          <div className="bg-gradient-to-r from-[#2E1065] to-[#4C1D95] px-6 py-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl">
                  <i className="ri-bar-chart-2-line text-white text-xl"></i>
                </div>
                <div>
                  <div className="text-white font-black text-lg">学习排行榜</div>
                  <div className="text-white/60 text-xs">历史成绩 · 进步曲线</div>
                </div>
              </div>
              <button
                onClick={() => setMode('config')}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/25 cursor-pointer transition-colors"
              >
                <i className="ri-arrow-left-line text-white"></i>
              </button>
            </div>
          </div>

          <div className="p-6">
            {leaderboard.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-gray-100 mx-auto mb-4">
                  <i className="ri-trophy-line text-3xl text-gray-300"></i>
                </div>
                <p className="text-gray-500 font-medium">还没有测试记录</p>
                <p className="text-gray-400 text-sm mt-1">完成第一次测试后，成绩将显示在这里</p>
                <button
                  onClick={startQuiz}
                  className="mt-4 px-6 py-2.5 bg-[#7C3AED] text-white rounded-xl font-bold text-sm cursor-pointer hover:bg-[#6D28D9] transition-colors whitespace-nowrap"
                >
                  立即开始测试
                </button>
              </div>
            ) : (
              <>
                {/* 最高分展示 + 分享 */}
                {bestRecord && (
                  <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-2xl p-4 mb-5">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 flex items-center justify-center rounded-full bg-amber-100 shrink-0">
                        <i className="ri-trophy-fill text-amber-500 text-2xl"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-amber-600 font-bold mb-0.5">历史最高分</div>
                        <div className="text-3xl font-black text-amber-700">{bestRecord.percentage}<span className="text-lg">%</span></div>
                        <div className="text-xs text-amber-600">{bestRecord.score}/{bestRecord.total} 题 · {bestRecord.difficulty} · {bestRecord.date}</div>
                      </div>
                      <button
                        onClick={() => handleShare(bestRecord.percentage, bestRecord.score, bestRecord.total, bestRecord.difficulty)}
                        className="flex flex-col items-center gap-1 px-3 py-2 bg-amber-100 hover:bg-amber-200 rounded-xl cursor-pointer transition-colors shrink-0"
                        title="分享最高分"
                      >
                        <i className={`text-lg ${shareSuccess ? 'ri-checkbox-circle-fill text-emerald-500' : 'ri-share-line text-amber-600'}`}></i>
                        <span className="text-xs text-amber-700 font-semibold whitespace-nowrap">
                          {shareSuccess ? '已复制！' : '分享'}
                        </span>
                      </button>
                    </div>
                  </div>
                )}

                {/* 进步曲线 */}
                {curveData.length >= 2 && (
                  <div className="mb-5">
                    <div className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                      <i className="ri-line-chart-line text-[#7C3AED]"></i>
                      进步曲线（最近{curveData.length}次）
                    </div>
                    <div className="bg-gray-50 rounded-xl p-4">
                      <div className="relative h-24">
                        {/* Y轴刻度 */}
                        <div className="absolute left-0 top-0 bottom-0 flex flex-col justify-between text-[10px] text-gray-400 pr-2">
                          <span>100%</span>
                          <span>50%</span>
                          <span>0%</span>
                        </div>
                        {/* 折线图 */}
                        <div className="ml-8 h-full relative">
                          <svg className="w-full h-full" viewBox={`0 0 ${(curveData.length - 1) * 40} 96`} preserveAspectRatio="none">
                            {/* 网格线 */}
                            <line x1="0" y1="48" x2={(curveData.length - 1) * 40} y2="48" stroke="#E5E7EB" strokeWidth="1" strokeDasharray="4,4" />
                            {/* 折线 */}
                            <polyline
                              points={curveData.map((r, i) => `${i * 40},${96 - (r.percentage / 100) * 96}`).join(' ')}
                              fill="none"
                              stroke="#7C3AED"
                              strokeWidth="2.5"
                              strokeLinejoin="round"
                              strokeLinecap="round"
                            />
                            {/* 填充区域 */}
                            <polygon
                              points={[
                                ...curveData.map((r, i) => `${i * 40},${96 - (r.percentage / 100) * 96}`),
                                `${(curveData.length - 1) * 40},96`,
                                `0,96`,
                              ].join(' ')}
                              fill="url(#curveGrad)"
                              opacity="0.3"
                            />
                            <defs>
                              <linearGradient id="curveGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#7C3AED" />
                                <stop offset="100%" stopColor="#7C3AED" stopOpacity="0" />
                              </linearGradient>
                            </defs>
                            {/* 数据点 */}
                            {curveData.map((r, i) => (
                              <circle
                                key={i}
                                cx={i * 40}
                                cy={96 - (r.percentage / 100) * 96}
                                r="4"
                                fill="white"
                                stroke="#7C3AED"
                                strokeWidth="2.5"
                              />
                            ))}
                          </svg>
                        </div>
                      </div>
                      {/* X轴标签 */}
                      <div className="ml-8 flex justify-between mt-1">
                        {curveData.map((r, i) => (
                          <span key={i} className="text-[9px] text-gray-400 font-bold">{r.percentage}%</span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* 历史记录列表 */}
                <div>
                  <div className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                    <i className="ri-history-line text-[#7C3AED]"></i>
                    历史记录（共{leaderboard.length}次）
                  </div>
                  <div className="space-y-2 max-h-52 overflow-y-auto">
                    {leaderboard.map((record, i) => {
                      const isPersonalBest = record.percentage === bestRecord?.percentage && record.timestamp === bestRecord?.timestamp;
                      return (
                        <div
                          key={record.timestamp}
                          className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${
                            isPersonalBest
                              ? 'bg-amber-50 border-amber-200'
                              : 'bg-gray-50 border-gray-100'
                          }`}
                        >
                          <div className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-black shrink-0 ${
                            i === 0 ? 'bg-[#7C3AED] text-white' : 'bg-gray-200 text-gray-500'
                          }`}>
                            {i + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className={`text-base font-black ${isPersonalBest ? 'text-amber-600' : 'text-gray-800'}`}>
                                {record.percentage}%
                              </span>
                              {isPersonalBest && <i className="ri-trophy-fill text-amber-500 text-sm"></i>}
                              <span className="text-xs text-gray-500">{record.score}/{record.total}题</span>
                              <span className="text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">{record.difficulty}</span>
                            </div>
                            <div className="text-xs text-gray-400">{record.date}</div>
                          </div>
                          {/* 进度条 */}
                          <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden shrink-0">
                            <div
                              className="h-full rounded-full"
                              style={{
                                width: `${record.percentage}%`,
                                background: record.percentage >= 90 ? '#F59E0B' : record.percentage >= 70 ? '#10B981' : record.percentage >= 50 ? '#7C3AED' : '#EF4444',
                              }}
                            ></div>
                          </div>
                          {/* 单条分享 */}
                          <button
                            onClick={() => handleShare(record.percentage, record.score, record.total, record.difficulty)}
                            className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 hover:bg-[#7C3AED]/10 text-gray-400 hover:text-[#7C3AED] cursor-pointer transition-colors shrink-0"
                            title="分享这次成绩"
                          >
                            <i className="ri-share-line text-xs"></i>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* 清除记录 */}
                <button
                  onClick={() => {
                    localStorage.removeItem(LEADERBOARD_KEY);
                    setLeaderboard([]);
                  }}
                  className="mt-4 w-full py-2.5 border border-gray-200 rounded-xl text-xs text-gray-400 hover:text-red-500 hover:border-red-200 cursor-pointer transition-colors whitespace-nowrap"
                >
                  <i className="ri-delete-bin-line mr-1.5"></i>
                  清除所有记录
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── 答题页 ──
  if (mode === 'playing' && currentQ) {
    const diffInfo = DIFFICULTY_LABELS[currentQ.difficulty];
    const progress = ((currentIdx + 1) / questions.length) * 100;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
        <div className="bg-white rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl">
          {/* 顶部进度 */}
          <div className="bg-gradient-to-r from-[#2E1065] to-[#4C1D95] px-5 py-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="text-white/70 text-sm">第 {currentIdx + 1} / {questions.length} 题</span>
                <span className={`text-xs px-2 py-0.5 rounded-full border font-semibold ${diffInfo.color}`}>{diffInfo.label}</span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-white/15 text-gray-500">{currentQ.category}</span>
              </div>
              <div className={`flex items-center gap-1.5 text-sm font-bold ${timeLeft <= 10 ? 'text-red-300 animate-pulse' : 'text-white/80'}`}>
                <i className="ri-time-line text-sm"></i>
                {timeLeft}s
              </div>
            </div>
            <div className="h-1.5 bg-white/20 rounded-full overflow-hidden">
              <div
                className="h-full bg-white rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
            {/* 计时条 */}
            <div className="h-1 bg-white/10 rounded-full overflow-hidden mt-1.5">
              <div
                className={`h-full rounded-full transition-all duration-1000 ${timeLeft <= 10 ? 'bg-red-400' : 'bg-emerald-400'}`}
                style={{ width: `${(timeLeft / 30) * 100}%` }}
              />
            </div>
          </div>

          <div className="p-5">
            {/* 题目 */}
            <div className="mb-5">
              <p className="text-gray-800 font-bold text-base leading-relaxed">{currentQ.question}</p>
            </div>

            {/* 选项 */}
            <div className="space-y-2.5 mb-4">
              {currentQ.options.map((opt, i) => {
                let style = 'bg-gray-50 border-gray-200 text-gray-700 hover:border-[#7C3AED]/40 hover:bg-[#7C3AED]/5';
                if (selectedOption !== null) {
                  if (i === currentQ.correctIndex) {
                    style = 'bg-emerald-50 border-emerald-400 text-emerald-800';
                  } else if (i === selectedOption && selectedOption !== currentQ.correctIndex) {
                    style = 'bg-red-50 border-red-400 text-red-800';
                  } else {
                    style = 'bg-gray-50 border-gray-200 text-gray-400';
                  }
                }

                return (
                  <button
                    key={i}
                    onClick={() => handleAnswer(i)}
                    disabled={selectedOption !== null}
                    className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-all cursor-pointer ${style} ${selectedOption === null ? 'cursor-pointer' : 'cursor-default'}`}
                  >
                    <div className="flex items-start gap-3">
                      <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold shrink-0 mt-0.5 ${
                        selectedOption !== null && i === currentQ.correctIndex
                          ? 'bg-emerald-500 text-white'
                          : selectedOption !== null && i === selectedOption && selectedOption !== currentQ.correctIndex
                          ? 'bg-red-500 text-white'
                          : 'bg-gray-200 text-gray-600'
                      }`}>
                        {selectedOption !== null && i === currentQ.correctIndex
                          ? <i className="ri-check-line text-xs"></i>
                          : selectedOption !== null && i === selectedOption && selectedOption !== currentQ.correctIndex
                          ? <i className="ri-close-line text-xs"></i>
                          : String.fromCharCode(65 + i)}
                      </span>
                      <span className="text-sm leading-relaxed">{opt}</span>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* 解析 */}
            {showExplanation && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                <div className="flex items-center gap-1.5 text-amber-700 font-bold text-sm mb-2">
                  <i className="ri-lightbulb-flash-line text-amber-500"></i>
                  解析
                </div>
                <p className="text-amber-800 text-sm leading-relaxed">{currentQ.explanation}</p>
              </div>
            )}

            {/* 下一题 */}
            {selectedOption !== null && (
              <button
                onClick={handleNext}
                className="w-full py-3 bg-[#7C3AED] text-white rounded-xl font-bold text-sm hover:bg-[#6D28D9] cursor-pointer transition-colors whitespace-nowrap"
              >
                {currentIdx + 1 >= questions.length ? '查看结果' : '下一题'}
                <i className="ri-arrow-right-line ml-2"></i>
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── 结果页 ──
  if (mode === 'result') {
    const percentage = Math.round((score / questions.length) * 100);
    const wrongQuestions = questions.filter((q, i) => answers[i] !== q.correctIndex);
    const allRecords = leaderboard;
    const bestEver = allRecords.length > 0 ? Math.max(...allRecords.map((r) => r.percentage)) : 0;
    const isNewBest = percentage >= bestEver && allRecords.length > 0;
    const diffLabel = selectedDifficulty === 'all' ? '全部' : selectedDifficulty === 'easy' ? '基础' : selectedDifficulty === 'medium' ? '进阶' : '专家';

    let resultLevel = { label: '继续加油', icon: 'ri-emotion-sad-line', color: 'text-rose-500', bg: 'bg-rose-50' };
    if (percentage >= 90) resultLevel = { label: '优秀！RWA专家', icon: 'ri-trophy-line', color: 'text-amber-500', bg: 'bg-amber-50' };
    else if (percentage >= 70) resultLevel = { label: '良好！掌握扎实', icon: 'ri-thumb-up-line', color: 'text-emerald-500', bg: 'bg-emerald-50' };
    else if (percentage >= 50) resultLevel = { label: '及格，还需努力', icon: 'ri-emotion-normal-line', color: 'text-amber-500', bg: 'bg-amber-50' };

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4 overflow-y-auto py-6">
        <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl">
          <div className="bg-gradient-to-r from-[#2E1065] to-[#4C1D95] px-6 py-5 text-center">
            <div className="text-white font-black text-xl mb-1">测试完成！</div>
            <div className="text-white/60 text-sm">共 {questions.length} 题 · 用时约 {questions.length * 30 / 60} 分钟</div>
          </div>

          <div className="p-6">
            {/* 分数展示 */}
            <div className={`${resultLevel.bg} rounded-2xl p-6 text-center mb-5 relative`}>
              {isNewBest && (
                <div className="absolute top-3 right-3 flex items-center gap-1 px-2.5 py-1 bg-amber-400 rounded-full">
                  <i className="ri-trophy-fill text-white text-xs"></i>
                  <span className="text-white text-xs font-black">新纪录！</span>
                </div>
              )}
              <div className={`w-16 h-16 flex items-center justify-center rounded-full bg-white mx-auto mb-3 ${resultLevel.color}`}>
                <i className={`${resultLevel.icon} text-3xl`}></i>
              </div>
              <div className="text-5xl font-black text-gray-800 mb-1">{percentage}<span className="text-2xl">%</span></div>
              <div className={`text-base font-bold ${resultLevel.color}`}>{resultLevel.label}</div>
              <div className="text-gray-500 text-sm mt-1">答对 {score} / {questions.length} 题</div>
              {bestEver > 0 && (
                <div className="mt-2 text-xs text-gray-400">
                  历史最高：<span className="font-bold text-amber-500">{bestEver}%</span>
                  {percentage > bestEver && <span className="text-emerald-500 ml-1">↑ 提升了 {percentage - bestEver}%</span>}
                </div>
              )}
            </div>

            {/* 分类统计 */}
            <div className="grid grid-cols-3 gap-3 mb-5">
              <div className="bg-emerald-50 rounded-xl p-3 text-center">
                <div className="text-2xl font-black text-emerald-600">{score}</div>
                <div className="text-xs text-emerald-700 font-medium">答对</div>
              </div>
              <div className="bg-red-50 rounded-xl p-3 text-center">
                <div className="text-2xl font-black text-red-500">{questions.length - score}</div>
                <div className="text-xs text-red-600 font-medium">答错</div>
              </div>
              <div className="bg-[#F8F7FF] rounded-xl p-3 text-center">
                <div className="text-2xl font-black text-[#7C3AED]">{percentage}%</div>
                <div className="text-xs text-[#7C3AED] font-medium">正确率</div>
              </div>
            </div>

            {/* 分享成绩卡片 */}
            <div className="mb-5 bg-gray-50 border border-gray-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <i className="ri-share-circle-line text-[#7C3AED] text-base"></i>
                  <span className="text-sm font-bold text-gray-700">分享成绩</span>
                </div>
                <button
                  onClick={() => handleShare(percentage, score, questions.length, diffLabel)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold cursor-pointer transition-all whitespace-nowrap ${
                    shareSuccess
                      ? 'bg-emerald-500 text-white'
                      : 'bg-[#7C3AED] text-white hover:bg-[#6D28D9]'
                  }`}
                >
                  <i className={`text-sm ${shareSuccess ? 'ri-checkbox-circle-fill' : 'ri-clipboard-line'}`}></i>
                  {shareSuccess ? '已复制！' : '复制成绩卡'}
                </button>
              </div>
              {/* 成绩卡预览 */}
              <div className="bg-white border border-gray-100 rounded-lg p-3 font-mono text-xs text-gray-600 leading-relaxed whitespace-pre-wrap">
                {generateShareText(percentage, score, questions.length, diffLabel)}
              </div>
            </div>

            {/* 错题回顾 */}
            {wrongQuestions.length > 0 && (
              <div className="mb-5">
                <div className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                  <i className="ri-error-warning-line text-red-500"></i>
                  错题回顾（{wrongQuestions.length}题）
                </div>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {wrongQuestions.map((q) => (
                    <div key={q.id} className="bg-red-50 border border-red-100 rounded-xl p-3">
                      <div className="text-sm font-medium text-gray-700 mb-1.5 line-clamp-2">{q.question}</div>
                      <div className="flex items-start gap-1.5 text-xs text-emerald-700">
                        <i className="ri-checkbox-circle-fill text-emerald-500 mt-0.5 shrink-0"></i>
                        <span>正确答案：{q.options[q.correctIndex]}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 操作按钮 */}
            <div className="flex gap-3">
              <button
                onClick={() => setMode('leaderboard')}
                className="flex-1 py-3 border border-[#7C3AED]/30 rounded-xl text-sm font-semibold text-[#7C3AED] hover:bg-[#7C3AED]/5 cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-bar-chart-2-line mr-1.5"></i>
                排行榜
              </button>
              <button
                onClick={() => setMode('config')}
                className="flex-1 py-3 border border-gray-200 rounded-xl text-sm font-semibold text-gray-600 hover:border-gray-300 cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-refresh-line mr-1.5"></i>
                再测一次
              </button>
              <button
                onClick={onClose}
                className="flex-1 py-3 bg-[#7C3AED] text-white rounded-xl text-sm font-bold hover:bg-[#6D28D9] cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-book-2-line mr-1.5"></i>
                回去复习
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── 错题本页 ──
  if (mode === 'wrongbook') {
    const filteredWrong = wrongBookFilter === '全部' ? wrongBook : wrongBook.filter((r) => r.category === wrongBookFilter);
    const categories = ['全部', ...Array.from(new Set(wrongBook.map((r) => r.category)))];
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4 overflow-y-auto py-6">
        <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl">
          <div className="bg-gradient-to-r from-[#7F1D1D] to-[#991B1B] px-6 py-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl">
                  <i className="ri-error-warning-line text-white text-xl"></i>
                </div>
                <div>
                  <div className="text-white font-black text-lg">错题本</div>
                  <div className="text-white/60 text-xs">共 {wrongBook.length} 道错题 · 专项练习</div>
                </div>
              </div>
              <button onClick={() => setMode('config')} className="w-8 h-8 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/25 cursor-pointer transition-colors">
                <i className="ri-arrow-left-line text-white"></i>
              </button>
            </div>
          </div>
          <div className="p-6">
            {wrongBook.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-emerald-50 mx-auto mb-4">
                  <i className="ri-checkbox-circle-line text-3xl text-emerald-400"></i>
                </div>
                <p className="text-gray-600 font-bold">错题本是空的！</p>
                <p className="text-gray-400 text-sm mt-1">答错的题目会自动收录到这里</p>
                <button onClick={startQuiz} className="mt-4 px-6 py-2.5 bg-[#7C3AED] text-white rounded-xl font-bold text-sm cursor-pointer hover:bg-[#6D28D9] transition-colors whitespace-nowrap">
                  开始测试
                </button>
              </div>
            ) : (
              <>
                {/* 分类筛选 */}
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {categories.map((cat) => (
                    <button key={cat} onClick={() => setWrongBookFilter(cat)}
                      className={`text-xs px-2.5 py-1 rounded-full cursor-pointer transition-all whitespace-nowrap ${wrongBookFilter === cat ? 'bg-red-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                      {cat}{cat === '全部' ? `(${wrongBook.length})` : `(${wrongBook.filter(r => r.category === cat).length})`}
                    </button>
                  ))}
                </div>
                {/* 错题列表 */}
                <div className="space-y-3 max-h-72 overflow-y-auto mb-4">
                  {filteredWrong.sort((a, b) => b.wrongCount - a.wrongCount).map((r) => {
                    const diffInfo = DIFFICULTY_LABELS[r.difficulty];
                    return (
                      <div key={r.questionId} className="bg-red-50 border border-red-100 rounded-xl p-3">
                        <div className="flex items-start justify-between gap-2 mb-1.5">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className={`text-xs px-1.5 py-0.5 rounded-full border font-semibold ${diffInfo.color}`}>{diffInfo.label}</span>
                            <span className="text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">{r.category}</span>
                            <span className="text-xs text-red-500 font-bold">错{r.wrongCount}次</span>
                          </div>
                          <button onClick={() => { removeFromWrongBook(r.questionId); setWrongBook(loadWrongBook()); }}
                            className="w-5 h-5 flex items-center justify-center rounded-full bg-gray-100 hover:bg-red-100 text-gray-400 hover:text-red-500 cursor-pointer transition-colors shrink-0">
                            <i className="ri-close-line text-xs"></i>
                          </button>
                        </div>
                        <p className="text-sm text-gray-700 font-medium line-clamp-2">{r.question}</p>
                        <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                          <i className="ri-checkbox-circle-fill text-emerald-500"></i>
                          {r.options[r.correctIndex]}
                        </p>
                      </div>
                    );
                  })}
                </div>
                {/* 专项练习按钮 */}
                <button
                  onClick={() => {
                    const pool = filteredWrong.map((r) => QUIZ_BANK.find((q) => q.id === r.questionId)).filter(Boolean) as QuizQuestion[];
                    if (pool.length === 0) return;
                    const shuffled = shuffle(pool);
                    setQuestions(shuffled);
                    setAnswers(new Array(shuffled.length).fill(null));
                    setCurrentIdx(0);
                    setSelectedOption(null);
                    setShowExplanation(false);
                    setTimeLeft(30);
                    setTimerActive(true);
                    setMode('playing');
                  }}
                  className="w-full py-3 bg-red-500 text-white rounded-xl font-bold text-sm hover:bg-red-600 cursor-pointer transition-colors whitespace-nowrap mb-2"
                >
                  <i className="ri-play-circle-line mr-2"></i>
                  专项练习（{filteredWrong.length}题）
                </button>
                <button onClick={() => { saveWrongBook([]); setWrongBook([]); }}
                  className="w-full py-2 border border-gray-200 rounded-xl text-xs text-gray-400 hover:text-red-500 hover:border-red-200 cursor-pointer transition-colors whitespace-nowrap">
                  <i className="ri-delete-bin-line mr-1"></i>清空错题本
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return null;
}
